import { Dropdown, Col, Row, Space, Typography, Modal, message } from "antd";
import { useState } from "react";
import AssessmentReportFormDialog from "../components/reports/AssessmentReportFormDialog";
import ReportReview from "../components/reports/ReportReview";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import { getAssessmentReportsExport } from "../repository/data.repository";
import Loader from "../controls/Loader";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import AssessmentReportsView from "../components/reports/AssessmentReportsView";
import { AssessmentReport } from "../repository/data.default";
import { getProblemMessage } from "../utils/http";

function AssessmentReportsPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [reportInputMode, setReportInputMode] = useState<
    "add" | "update" | "delete"
  >("add");
  const [reportInputOpen, setReportInputOpen] = useState(false);
  const [reportInputValues, setReportInputValues] = useState<AssessmentReport>(
    {} as any,
  );
  const [reviewReport, setReviewReport] = useState<AssessmentReport | null>(
    null,
  );
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onAction(key: string, record: AssessmentReport): void {
    if (key === "results") {
      // Both Base and Meta reports open the unified Report Review surface.
      setReviewReport(record);
      return;
    }
    setReportInputMode(key as "add" | "update" | "delete");
    setReportInputOpen(true);
    setReportInputValues(record || ({} as any));
  }

  function onReportInputOpen(mode: "add" | "update" | "delete"): void {
    setReportInputMode(mode);
    setReportInputOpen(true);
    setReportInputValues({} as any);
  }

  function onReportInputFinish(): void {
    setReportInputOpen(false);
    setReportInputValues({} as any);
    setTriggerView(Guid.create().toString());
  }

  function onReportInputClose(): void {
    setReportInputOpen(false);
    setReportInputValues({} as any);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading reports export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getAssessmentReportsExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Reports
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("Reports", "create")}

            className={`${!permissions.can("Reports", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Reports", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onReportInputOpen("add")}
          >
            Add Report
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <AssessmentReportsView
          trigger={triggerView}
          onAction={onAction}
          onSearch={onSearch}
        />
      </Col>
      <AssessmentReportFormDialog
        key={reportInputMode}
        mode={reportInputMode}
        open={reportInputOpen}
        initialValues={reportInputValues}
        onFinish={onReportInputFinish}
        onCancel={onReportInputClose}
      />
      <ReportReview
        open={!!reviewReport}
        report={reviewReport}
        onClose={() => setReviewReport(null)}
      />
      {contextHolder}
    </Col>
  );
}

export default AssessmentReportsPage;
